import { Button, Dropdown, Input, Select, Space, TableProps } from "antd";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import ActionIcon from "../../../assets/icons/ActionIcon";
import AddIcon from "../../../assets/icons/AddIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SendIcon from "../../../assets/icons/SendIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import TitleBar from "../../../components/common/TitleBar";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import {
  useArchiveInvoiceMutation,
  useGetInvoiceListQuery,
  useSendToApproverMutation,
} from "../../../features/Invoice/invoice.api";
import { InvoiceListDetails } from "../../../features/Invoice/invoice.type";
import { useGetVendorListQuery } from "../../../features/Vendor/vendor.api";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import {
  addCommaInNumber,
  countParPageSerialNumber,
} from "../../../utils/number";
import { capitalize, valueOrNa } from "../../../utils/string";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
} from "../../../utils/utils";
import InvoiceDetails from "./InvoiceDetails";

type TFilter = {
  vendor_id?: string;
  status?: string;
};

type TModalData = { id: number; title: string };

export const INVOICE_STATUS_CONFIG = {
  pending_for_approval: "status status-yellow",
  pending_for_disbursement: "status status-primary",
  unpaid: "status status-slate",
  paid: "status status-success",
  failed: "status status-error",
  draft: "status status-slate",
  archived: "status status-error",
  rejected: "status status-error",
  approved: "status status-success",
};

const InvoiceList = () => {
  const navigate = useNavigate();
  const { open, handleOpen, handleClose } = useModal<TModalData, "ARCHIVE">();

  const {
    page,
    limit,
    filter,
    keyword,
    handleFilter,
    handleLimit,
    handlePagination,
    handleSearch,
  } = useTable<TFilter>({});

  const {
    data: invoiceList,
    isLoading,
    isFetching,
  } = useGetInvoiceListQuery({
    params: {
      ...filter,
      page,
      limit,
      search: keyword,
    },
  });
  const { data: vendorList } = useGetVendorListQuery({
    params: { status: "active" },
  });

  const [
    archiveInvoice,
    {
      isLoading: archiving,
      isError: isArchiveError,
      error: archiveError,
      isSuccess: archived,
    },
  ] = useArchiveInvoiceMutation();

  const [
    sendToApproval,
    { isError: isSendError, error: sendError, isSuccess: sent },
  ] = useSendToApproverMutation();

  // ------ Effects ------
  useEffect(() => {
    if (archived) {
      toast("success", "Invoice archived successfully.");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archived]);

  useEffect(() => {
    if (sent) {
      toast("success", "Invoice sent to approval successfully.");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sent]);

  // ------ Errors ------
  useError(isArchiveError, archiveError, "Invoice archive failed.");
  useError(isSendError, sendError, "Invoice send to approval failed.");

  const columns: TableProps<InvoiceListDetails>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Invoice Id",
      dataIndex: "invoice_id",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Job Title",
      dataIndex: "job_title",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      render: (val) => valueOrNa(val?.business_name),
    },
    {
      title: "Reference ID",
      dataIndex: "reference_id",
      render: (val) => valueOrNa(val),
    },
    {
      title: "Amount [BDT]",
      dataIndex: "invoice_amount",
      render: (val) => addCommaInNumber(val),
    },
    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      render: (val) => {
        return (
          <span
            className={
              INVOICE_STATUS_CONFIG[
                String(
                  val as string,
                ).toLowerCase() as keyof typeof INVOICE_STATUS_CONFIG
              ]
            }
          >
            {capitalize(val?.split("_").join(" "))}
          </span>
        );
      },
    },
    {
      title: "Action",
      align: "center",
      width: 130,
      render: (invoice) => (
        <Space align="center">
          <Dropdown
            menu={{
              items: [
                ...(invoice.status === "archived"
                  ? []
                  : [
                      {
                        key: "view",
                        label: (
                          <Space>
                            <ViewIcon />
                            <span>View</span>
                          </Space>
                        ),
                        onClick: () =>
                          handleOpen({
                            type: "VIEW",
                            data: {
                              id: invoice.id,
                              title: `${invoice.invoice_id} ${invoice.name}`,
                            },
                          }),
                      },
                    ]),
                ...(invoice.status === "draft"
                  ? [
                      {
                        key: "edit",
                        label: (
                          <Space>
                            <EditIcon />
                            <span>Edit</span>
                          </Space>
                        ),
                        onClick: () =>
                          navigate(`/admin/invoices/list/edit/${invoice.id}`),
                      },
                      {
                        key: "archive",
                        label: (
                          <Space>
                            <DeleteIcon height={16} width={16} />
                            <span>Archive</span>
                          </Space>
                        ),
                        onClick: () =>
                          handleOpen({
                            type: "ARCHIVE",
                            data: {
                              id: invoice.id,
                              title: `${invoice.invoice_id} ${invoice.name}`,
                            },
                          }),
                      },
                      {
                        key: "send-to-approval",
                        label: (
                          <Space>
                            <SendIcon />
                            <span>Send to Approval</span>
                          </Space>
                        ),
                        onClick: () => sendToApproval({ id: invoice.id }),
                      },
                    ]
                  : []),
              ],
            }}
            placement="bottomRight"
            trigger={["click"]}
            overlayClassName="w-[180px]"
          >
            <Button className="size-8 p-0 border-none shadow-none">
              <ActionIcon />
            </Button>
          </Dropdown>
        </Space>
      ),
    },
  ];
  return (
    <SidebarLayout title="Payable Management">
      <TitleBar title="Invoices" subTitle="Manage vendor payment invoice">
        <Link to="add">
          <Button icon={<AddIcon />} className="button-primary">
            Add New
          </Button>
        </Link>
      </TitleBar>
      <CustomTable
        title={"Invoices List"}
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={invoiceList?.data.totalLength}
        dataSource={addKeyInArray(invoiceList?.data.invoices || [])}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        hideDifferentRowColor
        filterSection={
          <>
            <Input
              className="bg-input"
              suffix={<SearchIcon />}
              placeholder="Search name, id"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
            />
            <Select
              allowClear
              showSearch
              placeholder="Vendors"
              filterOption={selectSearchOption}
              options={selectArrayOption(
                vendorList?.data?.vendors || [],
                "business_name",
                "id",
              )}
              className="min-w-[90px] bg-select"
              rootClassName="icon-gray"
              onChange={(val) => handleFilter("vendor_id", val)}
              popupMatchSelectWidth={150}
              placement="bottomRight"
            />
            <Select
              allowClear
              placeholder="Status"
              options={[
                {
                  label: "Pending For Approval",
                  value: "pending_for_approval",
                },
                {
                  label: "Pending For Disbursement",
                  value: "pending_for_disbursement",
                },
                { label: "Unpaid", value: "unpaid" },
                { label: "Paid", value: "paid" },
                { label: "Failed", value: "failed" },
                { label: "Draft", value: "draft" },
                { label: "Archived", value: "archived" },
              ]}
              placement="bottomRight"
              className="bg-select"
              rootClassName="icon-gray"
              onChange={(val) => handleFilter("status", val)}
              popupMatchSelectWidth={220}
            />
          </>
        }
      />
      <ConfirmModal
        closable
        icon="DELETE"
        okText={"Yes, Delete"}
        confirmTitle={
          <>
            <span className="block">
              Are your sure you want to Archive this record?
            </span>
            <span className="block text-sm text-theme-gray-700 font-normal">
              {open.data?.title}
            </span>
          </>
        }
        questionText="This action cannot be undone. The data will be permanently Archive from your system."
        open={open.open && open.type === "ARCHIVE"}
        handleClose={handleClose}
        handleOk={() => archiveInvoice({ id: open?.data?.id })}
        okLoading={archiving}
      />

      <InvoiceDetails
        open={open.open && open.type === "VIEW"}
        handleClose={handleClose}
        data={open.data?.id}
        type="invoices"
      />
    </SidebarLayout>
  );
};

export default InvoiceList;
